/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useDispatch, useSelector } from "react-redux"
import { actions, actionTypes } from "store/duck/bulkUpload/actions"
import { getActionFail, getActionSuccess } from "store/duck/utils/actionUtils"
import { useEffect, useCallback, useRef, useContext } from "react"
import { AppState } from "store/store.type"
import _debounce from "lodash/debounce"
import _sortBy from "lodash/sortBy"
import { InventoryCategory } from "types/delv-platform-api/inventoryCategory"
import { TroyWebTracker } from "utils/tracking"
import { EventCategoryEng, EventActionEng } from "constants/tracking"
import { useLazyFetchInventoryCategoryQuery } from "queries/bulkUploadApi"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { BulkUploadContext } from "../../BulkUploadContext"

export const useInventoryCategories = (categoryName: string) => {
  const dispatch = useDispatch()
  const { bulkUploadType } = useContext(BulkUploadContext)

  const {
    flags: { fetchInventoryCategory: isRtkFetchInventoryCategoryEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const {
    data,
    isFetching,
    merchantID: categoryMerchantID,
    menuGroupID: categoryMenuGroupID,
  } = useSelector<
    AppState,
    {
      data: InventoryCategory[]
      merchantID: string
      isFetching: boolean
      menuGroupID?: string
    }
  >(state => state?.bulkUpload?.inventoryCategories || {})
  const {
    merchantID,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const [fetchInventoryCategoryRtk] = useLazyFetchInventoryCategoryQuery()

  const firstFetch = useRef(true)

  const fetchInventoryCategory = useCallback(
    async (categoryNameParam: string) => {
      const requestParams = {
        categoryName: categoryNameParam,
        menuGroupID,
      }

      // While migration flags are loading, do not fall back to old Redux: the enabled bit is
      // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
      // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
      // the trigger UI while loading so clicks are not silently dropped.
      if (!isRtkFlagLoading && isRtkFetchInventoryCategoryEnabled) {
        const pendingAction = {
          type: actionTypes.FETCH_INVENTORY_CATEGORY,
          payload: {
            merchantID,
            menuGroupID,
            categoryName: categoryNameParam,
          },
        }

        dispatch(pendingAction)

        const result = await fetchInventoryCategoryRtk({
          merchantID,
          merchantGroupID,
          categoryName: categoryNameParam,
          menuGroupID,
          bulkUploadType,
        })

        if (result.error) {
          dispatch({
            type: getActionFail(actionTypes.FETCH_INVENTORY_CATEGORY),
            meta: {
              previousAction: pendingAction,
            },
          })
        } else {
          dispatch({
            type: getActionSuccess(actionTypes.FETCH_INVENTORY_CATEGORY),
            payload: {
              data: result.data ?? { categories: [] },
            },
            meta: {
              previousAction: pendingAction,
            },
          })
        }

        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.fetchInventoryCategory,
          labels: {
            MERCHANT_ID: merchantID,
            IS_RTK_ENABLED: isRtkFetchInventoryCategoryEnabled,
            FROM_RTK_QUERY: true,
            FROM_OLD_REDUX: false,
            ACTION_NAME: "fetchInventoryCategory",
          },
        })
      } else if (!isRtkFlagLoading) {
        dispatch(
          actions.fetchInventoryCategory(
            requestParams,
            merchantID,
            merchantGroupID,
            bulkUploadType,
          ),
        )
        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.fetchInventoryCategory,
          labels: {
            MERCHANT_ID: merchantID,
            IS_RTK_ENABLED: isRtkFetchInventoryCategoryEnabled,
            FROM_RTK_QUERY: false,
            FROM_OLD_REDUX: true,
            ACTION_NAME: "fetchInventoryCategory",
          },
        })
      }
    },
    [
      dispatch,
      menuGroupID,
      merchantID,
      bulkUploadType,
      merchantGroupID,
      isRtkFlagLoading,
      isRtkFetchInventoryCategoryEnabled,
      fetchInventoryCategoryRtk,
    ],
  )

  // fetch once when open the drawer
  useEffect(() => {
    if (merchantID || menuGroupID) {
      fetchInventoryCategory("")
    }
  }, [fetchInventoryCategory, menuGroupID, merchantID])

  const onChangeCategoryName = useCallback(
    _debounce((categoryNameParam: string) => {
      fetchInventoryCategory(categoryNameParam)
    }, 500),
    [fetchInventoryCategory],
  )

  // skip the first fetch, and debounce fetching
  useEffect(() => {
    if (firstFetch.current) {
      firstFetch.current = false
      return
    }
    onChangeCategoryName(categoryName)
  }, [onChangeCategoryName, categoryName])

  const inventoryCategoryAvailable =
    (categoryMerchantID === merchantID && data?.length) ||
    (categoryMenuGroupID === menuGroupID && data?.length)

  return {
    isFetching,
    data: inventoryCategoryAvailable
      ? (_sortBy(data, "name") as InventoryCategory[])
      : [],
  }
}
